/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import {
  useForm,
  FieldValues,
  SubmitHandler,
  Controller
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Form, Row, Col } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';

import Button from 'components/base/Button';
import discountService from 'service/discount';
import { showAlert } from 'store/alert/alert.thunk';

interface AddSerialNumberProps {
  quantityTime: any;
  setOpenSerialNumber: (show: boolean) => void;
  selectedRow: any;
  isDiscounter: any;
  changedOrderItems: any;
  setChangedOrderItems: any;
  setOpenAmount: any;
  selectedRowId: any;
}

const AddSerialNumber = ({
  quantityTime,
  setOpenSerialNumber,
  selectedRow,
  isDiscounter,
  changedOrderItems,
  setChangedOrderItems,
  setOpenAmount,
  selectedRowId
}: AddSerialNumberProps) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const [loading, setLoading] = useState(false);

  const dispatch: Dispatch<any> = useDispatch();
  const inputRef = useRef<HTMLInputElement | any>(null);

  const formValues = watch();

  const validateDuplicate = (value: string, index: number) => {
    if (!value) return true;

    const allSerials = Object.entries(formValues)
      .filter(([key]) => key.startsWith('name_'))
      .map(([, val]) => val as string);

    const isDuplicateInForm =
      allSerials.filter((serial, idx) => serial === value && idx !== index)
        .length > 0;

    const isNotInExistingSerials = !selectedRow?.serial_number?.includes(value);

    if (isDuplicateInForm) return t('duplicate_serial_number');
    if (isNotInExistingSerials) return t('serial_number_does_not_exist');

    return true;
  };

  const onSubmit: SubmitHandler<FieldValues> = async data => {
    setLoading(true);

    const serialNumbers: string[] = Object.entries(data)
      .filter(([key]) => key.startsWith('name_'))
      .sort(([a], [b]) => {
        const numA = parseInt(a.split('_')[1], 10);
        const numB = parseInt(b.split('_')[1], 10);
        return numA - numB;
      })
      .map(([, value]) => value as string);

    if (isDiscounter) {
      if (quantityTime) {
        try {
          const resData = await discountService
            ?.getListCheckDiscountItem(selectedRowId?.barcode, +quantityTime)
            .then((res: any) => res);

          const percentage = resData?.percentage ?? 0;
          const discount_id = resData?.id ?? '';
          const discount_pid = resData?.pid ?? 0;

          const updatedData = changedOrderItems.map((item: any, index: any) => {
            const discountedCost =
              percentage > 0
                ? +selectedRow?.price_whosale * (1 - percentage / 100)
                : +selectedRow?.price_whosale;
            if (index === +selectedRowId?.rowId) {
              return {
                ...item,
                warehouse_id: selectedRow?.warehouse_id,
                warehouse_pid: selectedRow?.warehouse_pid || 0,
                product_id: selectedRow?.product_id,
                product_pid: selectedRow?.product_pid || 0,
                product_parent_id: selectedRow?.product_parent_id,
                product_parent_pid: selectedRow?.product_parent_pid || 0,
                quantity_fact: +quantityTime,
                price: selectedRow?.price,
                price_in: selectedRow?.price_in,
                // price_whosale: selectedRow?.price_whosale,
                nomenclature_id: selectedRow?.nomenclature_id,
                nomenclature_pid: selectedRow?.nomenclature_pid || 0,
                discount_percentage: percentage,
                cost: discountedCost,
                prevCost: +selectedRow?.price_whosale,
                update_price: percentage > 0 ? true : false,
                discount_id: discount_id,
                discount_pid: discount_pid || 0,
                serial_number: serialNumbers
              };
            }
            return item;
          });

          setChangedOrderItems(updatedData);
          setOpenAmount(false);
          setOpenSerialNumber(false);
        } catch (error) {
          console.log('error==>', error);
        }
      } else {
        dispatch(
          showAlert({
            title: t('select_product_add_quantity')
          })
        );
      }
    } else {
      if (quantityTime) {
        const updatedData = changedOrderItems.map((item: any, index: any) => {
          if (index === +selectedRowId.rowId) {
            return {
              ...item,
              warehouse_id: selectedRow?.warehouse_id,
              warehouse_pid: selectedRow?.warehouse_pid || 0,
              product_id: selectedRow?.product_id,
              product_pid: selectedRow?.product_pid || 0,
              product_parent_id: selectedRow?.product_parent_id,
              product_parent_pid: selectedRow?.product_parent_pid || 0,
              quantity_fact: +quantityTime,
              price: selectedRow?.price,
              price_in: selectedRow?.price_in,
              // price_whosale: selectedRow?.price_whosale,
              nomenclature_id: selectedRow?.nomenclature_id,
              nomenclature_pid: selectedRow?.nomenclature_pid || 0,
              discount_percentage: 0,
              prevCost: +selectedRow?.price_whosale,
              update_price: false,
              serial_number: serialNumbers
            };
          }
          return item;
        });

        setChangedOrderItems(updatedData);
        setOpenAmount(false);
        setOpenSerialNumber(false);
      } else {
        dispatch(
          showAlert({
            title: t('select_product_add_quantity')
          })
        );
      }
    }
  };

  useEffect(() => {
    if (inputRef?.current && quantityTime) {
      inputRef?.current?.focus();
      setTimeout(() => {
        inputRef?.current?.select();
      }, 500);
    }
  }, [quantityTime]);

  return (
    <Form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
    >
      {quantityTime &&
        Array.from({ length: +quantityTime }).map((_, index) => (
          <Row key={index} className="mb-3">
            <Col>
              <Controller
                name={`name_${index}`}
                control={control}
                rules={{
                  required: t('required_field'),
                  validate: value => validateDuplicate(value, index)
                }}
                render={({ field }) => (
                  <Form.Floating className="md-4">
                    <Form.Control
                      {...field}
                      ref={index === 0 ? inputRef : null}
                      type="text"
                      placeholder={`${t('serial_number')} ${index}`}
                      onFocus={e => e.target.select()}
                      isInvalid={!!errors[`name_${index}`]}
                    />
                    <label htmlFor={`name_${index}`}>
                      {t('serial_number')} ( {index + 1} )
                    </label>
                    {errors[`name_${index}`] && (
                      <Form.Control.Feedback type="invalid">
                        {errors[`name_${index}`]?.type === 'required'
                          ? t('required_field')
                          : t(errors[`name_${index}`]?.message as string) ||
                            t('invalid_input')}
                      </Form.Control.Feedback>
                    )}
                  </Form.Floating>
                )}
              />
            </Col>
          </Row>
        ))}
      <Button
        style={{ height: '48px', marginTop: 'auto' }}
        variant="primary"
        type="submit"
        loading={loading}
        loadingPosition="start"
      >
        {t('save')}
      </Button>
    </Form>
  );
};

export default AddSerialNumber;
